import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { RotateCcw, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const STATUS = {
  pending: { color: C.amber, icon: Clock, label: 'Pending review' },
  approved: { color: C.green, icon: CheckCircle, label: 'Approved — policy active' },
  rejected: { color: C.red, icon: XCircle, label: 'Declined' },
};

export default function ReinstatementPanel({ isStaff, onChanged }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [termsFor, setTermsFor] = useState(null);
  const [coverGap, setCoverGap] = useState(true);
  const [revisedPremium, setRevisedPremium] = useState('');
  const [conditionsText, setConditionsText] = useState('');

  const load = useCallback(() => {
    api.get('/payments/reinstatements').then(r => setData(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const request = async (policyId, policyNumber) => {
    setActingId(policyId);
    try {
      await api.post('/payments/reinstatements', { policy_id: policyId, note: 'Premium settled — requesting reinstatement of cover' });
      toast.success(`Reinstatement requested for ${policyNumber} — the underwriting team has been notified`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    }
    setActingId(null);
  };

  const decide = async (requestId, action, policyNumber) => {
    setActingId(requestId);
    try {
      const payload = action === 'reject'
        ? { reason: 'Underwriting review — risk no longer within appetite' }
        : {
            cover_gap_exclusion: coverGap,
            revised_annual_premium: revisedPremium ? parseFloat(revisedPremium) : null,
            conditions: conditionsText.split('\n').map(c => c.trim()).filter(Boolean),
          };
      const { data: res } = await api.post(`/payments/reinstatements/${requestId}/${action}`, payload);
      toast.success(action === 'approve'
        ? `${policyNumber} reinstated with terms — broker notified${res.supplementary_invoice ? ` · supplementary invoice ${res.supplementary_invoice} raised` : ''}`
        : 'Request declined — broker notified');
      setTermsFor(null);
      setCoverGap(true);
      setRevisedPremium('');
      setConditionsText('');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
    setActingId(null);
  };

  const termsSummary = (t) => {
    if (!t) return null;
    const parts = [];
    if (t.cover_gap_exclusion) parts.push('no-claims cover gap');
    if (t.revised_annual_premium != null) parts.push(`revised premium $${Number(t.revised_annual_premium).toLocaleString()}${t.supplementary_invoice ? ` (suppl. ${t.supplementary_invoice})` : ''}`);
    (t.conditions || []).forEach(c => parts.push(c));
    return parts.length ? parts.join(' · ') : null;
  };

  if (!data) return null;
  const items = isStaff ? (data.pending || []) : (data.eligible || []);
  const history = isStaff ? (data.processed || []) : (data.requests || []);
  if (!items.length && !history.length) return null;

  return (
    <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(26,76,124,0.25)' }} data-testid="reinstatement-panel">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.blue }}>
        <RotateCcw className="w-3.5 h-3.5" />Reinstatements
        {isStaff && (
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
            style={{ background: items.length ? 'rgba(232,160,32,0.12)' : 'rgba(29,158,117,0.1)', color: items.length ? C.amber : C.green }}
            data-testid="reinstatement-pending-count">{items.length} pending</span>
        )}
      </h3>

      {!isStaff && items.length > 0 && (
        <div className="space-y-2 mb-2" data-testid="reinstatement-eligible">
          {items.map(p => (
            <div key={p.policy_id} className="flex flex-wrap items-center gap-3 p-2.5 rounded-sm border"
              style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`reinstate-row-${p.policy_number}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold" style={{ color: C.navy }}>{p.business_name}</p>
                <p className="text-[10px] font-mono" style={{ color: C.slate }}>
                  {p.policy_number} · cancelled for non-payment · invoice {p.invoice_number}
                  {p.premium_settled
                    ? <span style={{ color: C.green }}> · premium settled ✓</span>
                    : <span style={{ color: C.red }}> · premium still outstanding</span>}
                </p>
              </div>
              <button onClick={() => request(p.policy_id, p.policy_number)}
                disabled={!p.premium_settled || actingId === p.policy_id}
                data-testid={`request-reinstatement-${p.policy_number}`}
                title={p.premium_settled ? '' : 'Settle the outstanding premium first'}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{ background: C.blue }}>
                {actingId === p.policy_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Request Reinstatement
              </button>
            </div>
          ))}
        </div>
      )}

      {isStaff && items.length > 0 && (
        <div className="space-y-2 mb-2" data-testid="reinstatement-pending-list">
          {items.map(r => (
            <div key={r.request_id} className="flex flex-wrap items-center gap-3 p-2.5 rounded-sm border"
              style={{ borderColor: 'rgba(232,160,32,0.25)', background: 'rgba(232,160,32,0.03)' }}
              data-testid={`reinstatement-request-${r.policy_number}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold" style={{ color: C.navy }}>{r.business_name}</p>
                <p className="text-[10px] font-mono" style={{ color: C.slate }}>
                  {r.policy_number} · broker {r.broker_name} · invoice {r.invoice_number} paid ✓
                </p>
                {r.note && <p className="text-[10px] italic" style={{ color: C.slate }}>"{r.note}"</p>}
              </div>
              <button onClick={() => decide(r.request_id, 'reject', r.policy_number)} disabled={actingId === r.request_id}
                data-testid={`reject-reinstatement-${r.policy_number}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm border text-[10px] font-semibold transition-colors hover:bg-slate-50 disabled:opacity-50"
                style={{ color: C.red, borderColor: 'rgba(226,75,74,0.3)' }}>
                <XCircle className="w-3 h-3" />Decline
              </button>
              <button onClick={() => setTermsFor(termsFor === r.request_id ? null : r.request_id)}
                data-testid={`approve-reinstatement-${r.policy_number}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: C.green }}>
                <CheckCircle className="w-3 h-3" />
                {termsFor === r.request_id ? 'Hide Terms' : 'Approve…'}
              </button>
              {termsFor === r.request_id && (
                <div className="w-full mt-1 p-3 rounded-sm border space-y-2.5"
                  style={{ borderColor: 'rgba(29,158,117,0.3)', background: 'rgba(29,158,117,0.03)' }}
                  data-testid={`reinstatement-terms-form-${r.policy_number}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Reinstatement terms</p>
                  <label className="flex items-center gap-2 text-[11px] cursor-pointer" style={{ color: C.navy }}>
                    <input type="checkbox" checked={coverGap} onChange={e => setCoverGap(e.target.checked)}
                      data-testid={`terms-cover-gap-${r.policy_number}`} className="accent-emerald-600" />
                    No-claims cover gap — exclude losses occurring during the cancellation period
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Revised annual premium (optional)</p>
                      <input type="text" inputMode="decimal" value={revisedPremium}
                        onChange={e => setRevisedPremium(e.target.value.replace(/[^\d.]/g, ''))}
                        data-testid={`terms-revised-premium-${r.policy_number}`}
                        placeholder="leave blank to keep current"
                        className="px-2 py-1.5 border rounded-sm text-xs font-mono w-52"
                        style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Additional conditions (one per line)</p>
                      <textarea value={conditionsText} onChange={e => setConditionsText(e.target.value)} rows={2}
                        data-testid={`terms-conditions-${r.policy_number}`}
                        placeholder="e.g. Direct debit mandate required for future premiums"
                        className="w-full px-2 py-1.5 border rounded-sm text-xs"
                        style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
                    </div>
                  </div>
                  {revisedPremium && (
                    <p className="text-[10px]" style={{ color: C.amber }}>
                      A supplementary invoice will be raised automatically if the revised premium exceeds the current one.
                    </p>
                  )}
                  <button onClick={() => decide(r.request_id, 'approve', r.policy_number)} disabled={actingId === r.request_id}
                    data-testid={`confirm-approve-${r.policy_number}`}
                    className="flex items-center gap-1 px-4 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: C.green }}>
                    {actingId === r.request_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    Confirm Approval with Terms
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="pt-1" data-testid="reinstatement-history">
          <p className="text-[9px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>
            {isStaff ? 'Recently processed' : 'My requests'}
          </p>
          {history.slice(0, 5).map(r => {
            const s = STATUS[r.status] || STATUS.pending;
            const SIcon = s.icon;
            return (
              <div key={r.request_id} className="text-[10px]" data-testid={`reinstatement-history-${r.policy_number}`}>
                <p className="flex items-center gap-1.5" style={{ color: C.slate }}>
                  <SIcon className="w-3 h-3" style={{ color: s.color }} />
                  {r.policy_number} — {r.business_name} · <span style={{ color: s.color }}>{s.label}</span>
                  {r.status === 'rejected' && r.reject_reason ? ` (${r.reject_reason})` : ''}
                </p>
                {r.status === 'approved' && termsSummary(r.terms) && (
                  <p className="pl-[18px] italic" style={{ color: C.slate }} data-testid={`reinstatement-terms-${r.policy_number}`}>
                    Subject to: {termsSummary(r.terms)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
